import React, { useEffect, useRef, useState } from "react";
import Layout from "../../../layout/Layout";
import Loader from "../../../common/loader";
import Modal from "../../../common/modal";
import { toast } from "react-toastify";
import { FaRegEdit, FaInstagram, FaFacebookF, FaTiktok } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { API_PATH } from "../../../api/apiPath";
import { deleteApi, getApi, postApi, putApi } from "../../../api/axiosInstance";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

function ContactUs() {
  const { translations, dir } = useSelector((state) => state.language);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [instagramLink, setInstagramLink] = useState("");
  const [facebookLink, setFacebookLink] = useState("");
  const [tiktokLink, setTiktokLink] = useState("");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getApi(`${API_PATH.ADMIN.GET_CONTACT_INFO}`);
      setContactInfo(res.data?.data?.contact_info || null);
    } catch (err) {
      toast.error(translations.toaster.fetchInfoFailed);
      setContactInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Enhanced validation function with URL validation
  const validateInputs = () => {
    if (!email || email.trim() === "") {
      toast.error(translations.toaster.emailRequired);
      return false;
    }
    if (!phone || phone.trim() === "") {
      toast.error(translations.toaster.phoneNumberRequired);
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error(translations.toaster.emailValidError);
      return false;
    }

    // Phone number validation - must start with + and have country code
    const phoneRegex = /^\+[1-9]\d{1,14}$/; // E.164 format: + followed by 1-15 digits
    const trimmedPhone = phone.trim();

    if (!trimmedPhone.startsWith("+")) {
      toast.error(translations.toaster.phoneCountryCode);
      return false;
    }

    if (!phoneRegex.test(trimmedPhone)) {
      toast.error(translations.toaster.phoneValidError);
      return false;
    }

    // Additional check for minimum length (country code + number)
    if (trimmedPhone.length < 10) {
      toast.error(translations.toaster.phoneShortLength);
      return false;
    }

    // URL validation for social media links (optional fields)
    const urlRegex = /^https?:\/\/.+/;
    
    if (instagramLink.trim() && !urlRegex.test(instagramLink.trim())) {
      toast.error(translations.toaster.instagramLinkInvalid || "Invalid Instagram URL");
      return false;
    }

    if (facebookLink.trim() && !urlRegex.test(facebookLink.trim())) {
      toast.error(translations.toaster.facebookLinkInvalid || "Invalid Facebook URL");
      return false;
    }

    if (tiktokLink.trim() && !urlRegex.test(tiktokLink.trim())) {
      toast.error(translations.toaster.tiktokLinkInvalid || "Invalid TikTok URL");
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setEmail("");
    setPhone("");
    setInstagramLink("");
    setFacebookLink("");
    setTiktokLink("");
  };

  const handleAdd = async () => {
    if (!validateInputs()) return;
    setLoading(true);
    try {
      const payload = {
        email: email.trim(),
        fullPhoneNumber: phone.trim(),
        instagramLink: instagramLink.trim(),
        facebookLink: facebookLink.trim(),
        tiktokLink: tiktokLink.trim(),
      };
      const response = await postApi(
        `${API_PATH.ADMIN.ADD_CONTACT_INFO}`,
        payload
      );
      toast.success(translations.toaster.contactAddedSuccessfully);
      await fetchData(); // Refresh data
      setIsAddModalOpen(false);
      resetForm();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        translations.toaster.contactFailedToAdd;
      toast.error(
        Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateInputs()) return;
    setLoading(true);
    try {
      const payload = {
        email: email.trim(),
        fullPhoneNumber: phone.trim(),
        instagramLink: instagramLink.trim(),
        facebookLink: facebookLink.trim(),
        tiktokLink: tiktokLink.trim(),
      };
      const response = await putApi(
        `${API_PATH.ADMIN.UPDATE_CONTACT_INFO}`,
        payload
      );
      toast.success(translations.toaster.contactUpdatedSuccessfully);
      await fetchData(); // Refresh data
      setIsUpdateModalOpen(false);
      resetForm();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        translations.toaster.contactFailedToUpdate;
      toast.error(
        Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteApi(`${API_PATH.ADMIN.DELETE_CONTACT_INFO}`);
      toast.success(translations.toaster.contactDeletedSuccessfully);
      await fetchData(); // Refresh data
      setIsDeleteModalOpen(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        translations.toaster.contactFailedToDelete;
      toast.error(
        Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
      );
    } finally {
      setLoading(false);
    }
  };

  const openUpdateModal = () => {
    if (contactInfo) {
      setEmail(contactInfo.email || "");
      setPhone(contactInfo.phone_number || "");
      setInstagramLink(contactInfo.instagram_link || "");
      setFacebookLink(contactInfo.facebook_link || "");
      setTiktokLink(contactInfo.tiktok_link || "");
      setIsUpdateModalOpen(true);
    }
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    resetForm();
  };

  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    resetForm();
  };

  // Helper function to format social media links
  const formatSocialMediaLinks = () => {
    const links = [];
    
    if (contactInfo.instagram_link) {
      links.push(
        <a
          key="instagram"
          href={contactInfo.instagram_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-300 hover:text-blue-400 underline"
        >
          <FaInstagram className="inline mr-1 text-pink-500" />
          {translations.contactUs.instagram || "Instagram"}
        </a>
      );
    }

    if (contactInfo.facebook_link) {
      links.push(
        <a
          key="facebook"
          href={contactInfo.facebook_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-300 hover:text-blue-400 underline"
        >
          <FaFacebookF className="inline mr-1 text-blue-600" />
          {translations.contactUs.facebook || "Facebook"}
        </a>
      );
    }

    if (contactInfo.tiktok_link) {
      links.push(
        <a
          key="tiktok"
          href={contactInfo.tiktok_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-300 hover:text-blue-400 underline"
        >
          <FaTiktok className="inline mr-1 text-black bg-white rounded-full p-1" />
          {translations.contactUs.tiktok || "TikTok"}
        </a>
      );
    }

    return links.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {links.map((link, index) => (
          <span key={index}>
            {link}
            {index < links.length - 1 && <span className="text-gray-400 mx-1">,</span>}
          </span>
        ))}
      </div>
    ) : (
      <span className="text-gray-400">No social media links</span>
    );
  };

  return (
    <>
      <Layout>
        <div className="min-h-screen w-full px-4 py-4">
          <section className="pt-1 grid grid-cols-2 items-center">
            <h1 className="text-xl font-bold text-white">
              {translations.contactUs.contact_info}
              <small className="text-2xl font-semibold text-gray-600"> |</small>
              <small className="font-normal text-gray-600">
                {" "}
                {translations.contactUs.contact_management}
              </small>
            </h1>

            <div className="text-md text-white flex items-center justify-end space-x-1 ">
              <div className="rounded-full bg-[#5A189A] py-2 px-3">
                <Link
                  to="/"
                  className=" text-white  no-underline cursor-pointer"
                >
                  {translations.contactUs.dashboard}
                </Link>
                <span className="mx-1">/</span>
                <span className="text-green-300 ">
                  {translations.contactUs.contact_info}
                </span>
              </div>
            </div>
          </section>
          <div className="p-4 w-full bg-gray-800 shadow-xl rounded mt-5">
            {loading ? (
              <Loader />
            ) : contactInfo ? (
              <div className="flex flex-col gap-4 text-white">
                {/* Contact Info */}
                <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
                  {/* Left: Contact Info */}
                  <div className="flex-1">
                    <div className="space-y-3">
                      <p className="mb-2">
                        <strong>{translations.contactUs.email}:</strong>{" "}
                        {contactInfo.email}
                      </p>
                      <p className="mb-2">
                        <strong>{translations.contactUs.phone}:</strong>{" "}
                        <span dir="ltr">{contactInfo.phone_number}</span>
                      </p>
                      <div>
                        <strong>{translations.contactUs.social_media || "Social Media"}:</strong>{" "}
                        {formatSocialMediaLinks()}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-400 mt-4 max-w-md">
                      <strong className="text-red-400">
                        {translations.contactUs.note} :{" "}
                      </strong>
                      <em>{translations.contactUs.note_desc}</em>
                    </p>
                  </div>

                  {/* Right: Buttons */}
                  <div className="flex gap-3 mt-2 md:mt-0">
                    <button
                      onClick={openUpdateModal}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:bg-gray-500"
                      disabled={loading}
                    >
                      <FaRegEdit className="inline-block mr-1" />{" "}
                      {translations.contactUs.edit}
                    </button>
                    <button
                      onClick={() => setIsDeleteModalOpen(true)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded disabled:bg-gray-500"
                      disabled={loading}
                    >
                      <MdDelete className="inline-block mr-1" />{" "}
                      {translations.contactUs.delete}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400 mt-3">
                <p>{translations.contactUs.noData}</p>
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded disabled:bg-gray-500"
                  disabled={loading}
                >
                  {translations.contactUs.add_contact}
                </button>
                <p className="text-sm text-gray-400 mt-4 max-w-md mx-auto">
                  <strong className="text-red-400">
                    {translations.contactUs.note} :{" "}
                  </strong>
                  <em>{translations.contactUs.note_desc}</em>
                </p>
              </div>
            )}
          </div>
        </div>
      </Layout>

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onClose={closeAddModal}
      width="w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] max-w-6xl">
        <div className="p-6 text-white w-full">
          <h3 className="text-xl mb-4">{translations.contactUs.add_contact}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Basic Info */}
            <div>
              <h4 className="text-lg font-semibold mb-3 text-blue-300">Basic Information</h4>
              
              {/* Email Field */}
              <label className="block mb-2 text-sm text-gray-400">
                {translations.contactUs.email} *
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mb-4 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="email"
                placeholder={translations.contactUs.enter_email}
                required
              />
              
              {/* Phone Field */}
              <label className="block mb-2 text-sm text-gray-400">
                {translations.contactUs.phone_number} *
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`w-full mb-2 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none
                ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                type="text"
                placeholder={translations.contactUs.enter_phone}
                dir="ltr"
                required
              />
              <p className="text-xs text-gray-400 mb-4">
                {translations.contactUs.phone_note}
              </p>
            </div>

            {/* Right Column - Social Media */}
            <div>
              <h4 className="text-lg font-semibold mb-3 text-blue-300">
                {translations.contactUs.social_media || "Social Media Links"}
              </h4>
              
              {/* Instagram Field */}
              <label className="block mb-2 text-sm text-gray-400">
                <FaInstagram className="inline mr-2 text-pink-500" />
                {translations.contactUs.instagram || "Instagram URL"}
              </label>
              <input
                value={instagramLink}
                onChange={(e) => setInstagramLink(e.target.value)}
                className="w-full mb-4 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="url"
                placeholder="https://www.instagram.com/yourprofile"
                dir="ltr"
              />

              {/* Facebook Field */}
              <label className="block mb-2 text-sm text-gray-400">
                <FaFacebookF className="inline mr-2 text-blue-600" />
                {translations.contactUs.facebook || "Facebook URL"}
              </label>
              <input
                value={facebookLink}
                onChange={(e) => setFacebookLink(e.target.value)}
                className="w-full mb-4 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="url"
                placeholder="https://www.facebook.com/yourprofile"
                dir="ltr"
              />

              {/* TikTok Field */}
              <label className="block mb-2 text-sm text-gray-400">
                <FaTiktok className="inline mr-2 text-white bg-black rounded-full p-1" />
                {translations.contactUs.tiktok || "TikTok URL"}
              </label>
              <input
                value={tiktokLink}
                onChange={(e) => setTiktokLink(e.target.value)}
                className="w-full mb-2 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="url"
                placeholder="https://www.tiktok.com/yourprofile"
                dir="ltr"
              />
              
              <p className="text-xs text-gray-400 mb-4">
                {translations.contactUs.social_media_note || "Social media links are optional. Please provide complete URLs starting with https://"}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={closeAddModal}
              className="bg-gray-500 hover:bg-gray-600 px-4 py-2 rounded disabled:bg-gray-400"
              disabled={loading}
            >
              {translations.contactUs.cancel}
            </button>
            <button
              onClick={handleAdd}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded disabled:bg-gray-400"
              disabled={loading}
            >
              {loading
                ? translations.contactUs.adding
                : translations.contactUs.add}
            </button>
          </div>
        </div>
      </Modal>

      {/* Update Modal */}
      <Modal isOpen={isUpdateModalOpen} onClose={closeUpdateModal}
      width="w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] max-w-6xl">
        <div className="p-6 text-white w-full">
          <h3 className="text-xl mb-4">
            {translations.contactUs.update_contact}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Basic Info */}
            <div>
              <h4 className="text-lg font-semibold mb-3 text-blue-300">Basic Information</h4>
              
              {/* Email Field */}
              <label className="block mb-2 text-sm text-gray-400">
                {translations.contactUs.email} *
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mb-4 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="email"
                placeholder={translations.contactUs.enter_email}
                required
              />
              
              {/* Phone Field */}
              <label className="block mb-2 text-sm text-gray-400">
                {translations.contactUs.phone_number} *
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`w-full mb-2 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none
                ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                type="text"
                placeholder={translations.contactUs.enter_phone}
                dir="ltr"
                required
              />
              <p className="text-xs text-gray-400 mb-4">
                {translations.contactUs.phone_note}
              </p>
            </div>

            {/* Right Column - Social Media */}
            <div>
              <h4 className="text-lg font-semibold mb-3 text-blue-300">
                {translations.contactUs.social_media || "Social Media Links"}
              </h4>
              
              {/* Instagram Field */}
              <label className="block mb-2 text-sm text-gray-400">
                <FaInstagram className="inline mr-2 text-pink-500" />
                {translations.contactUs.instagram || "Instagram URL"}
              </label>
              <input
                value={instagramLink}
                onChange={(e) => setInstagramLink(e.target.value)}
                className="w-full mb-4 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="url"
                placeholder="https://www.instagram.com/yourprofile"
                dir="ltr"
              />

              {/* Facebook Field */}
              <label className="block mb-2 text-sm text-gray-400">
                <FaFacebookF className="inline mr-2 text-blue-600" />
                {translations.contactUs.facebook || "Facebook URL"}
              </label>
              <input
                value={facebookLink}
                onChange={(e) => setFacebookLink(e.target.value)}
                className="w-full mb-4 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="url"
                placeholder="https://www.facebook.com/yourprofile"
                dir="ltr"
              />

              {/* TikTok Field */}
              <label className="block mb-2 text-sm text-gray-400">
                <FaTiktok className="inline mr-2 text-white bg-black rounded-full p-1" />
                {translations.contactUs.tiktok || "TikTok URL"}
              </label>
              <input
                value={tiktokLink}
                onChange={(e) => setTiktokLink(e.target.value)}
                className="w-full mb-2 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
                type="url"
                placeholder="https://www.tiktok.com/yourprofile"
                dir="ltr"
              />
              
              <p className="text-xs text-gray-400 mb-4">
                {translations.contactUs.social_media_note || "Social media links are optional. Please provide complete URLs starting with https://"}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={closeUpdateModal}
              className="bg-gray-500 hover:bg-gray-600 px-4 py-2 rounded disabled:bg-gray-400"
              disabled={loading}
            >
              {translations.contactUs.cancel}
            </button>
            <button
              onClick={handleUpdate}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded disabled:bg-gray-400"
              disabled={loading}
            >
              {loading
                ? translations.contactUs.updating
                : translations.contactUs.update}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-6 text-white">
          <h3 className="text-xl text-red-600 mb-4 text-center">
            {translations.contactUs.confirm_delete}
          </h3>
          <p className="mb-6 text-center text-gray-300">
            {translations.contactUs.delete_warning}
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="bg-gray-500 hover:bg-gray-600 px-4 py-2 rounded disabled:bg-gray-400"
              disabled={loading}
            >
              {translations.contactUs.cancel}
            </button>
            <button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded disabled:bg-gray-400"
              disabled={loading}
            >
              {loading
                ? translations.contactUs.deleting
                : translations.contactUs.delete}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ContactUs;